import { useEffect, useState } from "react";
import { Trophy, Target, LogIn } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { googleLogin } from "@/components/Navbar";
import Reveal from "@/components/Reveal";

export default function Pickems() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [board, setBoard] = useState([]);

  const load = () => {
    api.get("/pickems").then((r) => setItems(r.data)).catch(() => {});
    api.get("/pickems/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [user]);

  const vote = async (matchId, choice) => {
    if (!user) {
      toast.error("Connectez-vous avec Google pour voter");
      return;
    }
    try {
      await api.post("/pickems", { match_id: matchId, choice });
      toast.success("Pronostic enregistré !");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (items.length === 0 && board.length === 0) return null;

  return (
    <section className="mt-16" data-testid="pickems-section">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Pick'ems</p>
        <h2 className="mt-2 text-2xl font-extrabold uppercase tracking-tight sm:text-3xl">Pronostiquez nos matchs</h2>
        <p className="mt-2 max-w-xl text-sm text-slate-400">
          Victoire ou défaite ? Votez avant le coup d'envoi et grimpez au classement des supporters.
          {!user && " Connectez-vous avec Google pour participer."}
        </p>
      </Reveal>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <div className="grid gap-4 lg:col-span-2">
          {items.map(({ match: m, votes, my_choice }) => {
            const total = votes.win + votes.loss;
            const pctWin = total ? Math.round((votes.win / total) * 100) : 50;
            return (
              <div key={m.id} className="card-tactical p-5" data-testid={`pickem-card-${m.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-heading text-lg font-bold uppercase">
                    Emisphere <span className="text-orange-500">vs</span> {m.opponent}
                  </p>
                  <span className="text-xs text-slate-500">{m.game} — {m.date}</span>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <button onClick={() => vote(m.id, "win")} data-testid={`pickem-vote-win-${m.id}`}
                    className={`rounded-xl border px-4 py-3 font-heading text-base font-bold uppercase transition-all hover:scale-[1.02] ${
                      my_choice === "win" ? "glow-orange border-orange-500 bg-orange-500 text-[#060B18]" : "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                    }`}>
                    Victoire {total > 0 && <span className="ml-1 font-mono2 text-sm">{pctWin}%</span>}
                  </button>
                  <button onClick={() => vote(m.id, "loss")} data-testid={`pickem-vote-loss-${m.id}`}
                    className={`rounded-xl border px-4 py-3 font-heading text-base font-bold uppercase transition-all hover:scale-[1.02] ${
                      my_choice === "loss" ? "glow-orange border-orange-500 bg-orange-500 text-[#060B18]" : "border-red-500/40 text-red-400 hover:bg-red-500/10"
                    }`}>
                    Défaite {total > 0 && <span className="ml-1 font-mono2 text-sm">{100 - pctWin}%</span>}
                  </button>
                </div>
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-800">
                  <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${pctWin}%` }} />
                </div>
                {my_choice && <p className="mt-2 text-[11px] font-bold uppercase text-orange-400">Votre pronostic : {my_choice === "win" ? "Victoire" : "Défaite"}</p>}
              </div>
            );
          })}
          {items.length === 0 && (
            <p className="text-sm text-slate-500">Aucun match ouvert aux pronostics pour le moment.</p>
          )}
        </div>

        <Reveal delay={0.1}>
          <div className="card-tactical h-fit p-5" data-testid="pickems-leaderboard">
            <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
              <Trophy className="h-5 w-5 text-orange-500" /> Top supporters
            </p>
            {board.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">Le classement apparaîtra après les premiers résultats.</p>
            ) : (
              <div className="mt-4 grid gap-2">
                {board.map((b, i) => (
                  <div key={b.name + i} data-testid={`pickems-board-${i}`}
                    className="flex items-center gap-3 rounded-lg border border-slate-700/60 bg-[#060B18] px-3 py-2">
                    <span className={`font-heading text-lg font-black ${i === 0 ? "text-orange-500" : "text-slate-500"}`}>#{i + 1}</span>
                    <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-200">{b.name}</span>
                    <span className="font-mono2 text-sm font-bold text-orange-400">{b.points} pts</span>
                  </div>
                ))}
              </div>
            )}
            {!user && (
              <button onClick={googleLogin} data-testid="pickems-login"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-orange-500/40 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-orange-400 transition-all hover:bg-orange-500/10">
                <LogIn className="h-4 w-4" /> Connexion Google pour voter
              </button>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
